/**
 * Componente de filtros para el listado de merchants.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE §Pantalla 1
 */

import { useCallback, useState } from 'react';
import { Search, Filter, Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from '@flexibilitypay/ui';

import type { MerchantStatus } from '../types/merchant.types';

export interface MerchantFiltersProps {
  onSearch: (search: string) => void;
  onStatusFilter: (status: MerchantStatus | undefined) => void;
  onExport: () => void;
  isExporting?: boolean;
  currentSearch?: string;
  currentStatus?: MerchantStatus;
}

const STATUS_OPTIONS: { value: MerchantStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVE', label: 'Activo' },
  { value: 'INACTIVE', label: 'Inactivo' },
  { value: 'CLOSED', label: 'Baja' },
];

export function MerchantFilters({
  onSearch,
  onStatusFilter,
  onExport,
  isExporting = false,
  currentSearch = '',
  currentStatus,
}: MerchantFiltersProps) {
  const { t } = useTranslation(['merchants', 'common']);
  const [searchValue, setSearchValue] = useState(currentSearch);
  const [showFilters, setShowFilters] = useState(false);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearch(searchValue);
    },
    [searchValue, onSearch],
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
    onSearch('');
  }, [onSearch]);

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as MerchantStatus | '';
      onStatusFilter(value || undefined);
    },
    [onStatusFilter],
  );

  return (
    <div className="mb-6 space-y-4">
      {/* Barra principal */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Búsqueda */}
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[280px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('merchants:filters.searchPlaceholder', 'Buscar por nombre o CUIT...')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchValue && (
              <button
                type="button"
                onClick={handleSearchClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>

        {/* Botones de acción */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="mr-2 h-4 w-4" />
            {t('merchants:filters.filter', 'Filtrar')}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={isExporting}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting
              ? t('common:exporting', 'Exportando...')
              : t('merchants:filters.export', 'Exportar CSV')}
          </Button>
        </div>
      </div>

      {/* Panel de filtros expandible */}
      {showFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label htmlFor="status-filter">
                {t('merchants:filters.status', 'Estado')}
              </Label>
              <select
                id="status-filter"
                value={currentStatus || ''}
                onChange={handleStatusChange}
                className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onStatusFilter(undefined);
                setSearchValue('');
                onSearch('');
              }}
            >
              {t('merchants:filters.clearAll', 'Limpiar filtros')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
